import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useImages } from "@/context/SiteContent";

const PEEKS = [
  ["Contents", "All 52 lessons across the 5Ps", "/peek-1.jpg"],
  ["People", "05 · Reliable vs Smart", "/peek-2.jpg"],
  ["Profit", "13 · Your First Paycheck", "/peek-3.jpg"],
];

export const Book3D = () => {
  const zone = useRef(null);
  const images = useImages();
  const book = useRef(null);
  const shine = useRef(null);
  const disc = useRef(null);
  const raf = useRef(null);
  const swipe = useRef({ x: 0, y: 0, done: false });
  const [swipeHint, setSwipeHint] = useState(false);
  const showSwipeHint = () => {
    const touch = window.matchMedia("(pointer: coarse)").matches;
    if (!touch || localStorage.getItem("tw_swipe_hint")) return;
    localStorage.setItem("tw_swipe_hint", "1");
    setSwipeHint(true);
    setTimeout(() => setSwipeHint(false), 3200);
  };
  const [opened, setOpened] = useState(false);
  const [page, setPage] = useState(0);
  const peek = PEEKS[page];

  const tilt = (cx, cy) => {
    if (opened || !zone.current) return;
    const r = zone.current.getBoundingClientRect();
    const x = (cx - r.left) / r.width - 0.5;
    const y = (cy - r.top) / r.height - 0.5;
    if (raf.current) cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(() => {
      const lift = Math.min(10, Math.abs(x) * 7 + Math.abs(y) * 7);
      book.current.style.transform = `rotateY(${x * 22 - 8}deg) rotateX(${-y * 14 + 2}deg) translateY(${-lift}px) translateZ(18px)`;
      shine.current.style.transform = `translate3d(${(x - 0.1) * 34}%,${(y - 0.5) * 10}%,10px)`;
      shine.current.style.opacity = ".88";
      disc.current.style.transform = `translateX(${x * 18}px) translateZ(-80px) scaleX(${1.06 + Math.abs(x) * 0.12}) scaleY(${0.94 - Math.abs(y) * 0.08})`;
      zone.current.classList.add("is-tilting");
    });
  };

  const reset = () => {
    if (opened || !zone.current) return;
    if (raf.current) cancelAnimationFrame(raf.current);
    book.current.style.transform = "rotateY(-8deg) rotateX(2deg) translateY(0) translateZ(0)";
    shine.current.style.transform = "translate3d(-32%,0,10px)";
    shine.current.style.opacity = "";
    disc.current.style.transform = "";
    zone.current.classList.remove("is-tilting");
  };

  const flip = (dir) => setPage((p) => (p + dir + PEEKS.length) % PEEKS.length);
  const onSwipeStart = (e) => { const t = e.touches?.[0]; if (t) swipe.current = { x: t.clientX, y: t.clientY, done: false }; };
  const onSwipeEnd = (e) => {
    const t = e.changedTouches?.[0];
    if (!opened || !t) return;
    const dx = t.clientX - swipe.current.x, dy = t.clientY - swipe.current.y;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) { swipe.current.done = true; setSwipeHint(false); flip(dx < 0 ? 1 : -1); }
  };

  const toggle = () => {
    if (swipe.current.done) { swipe.current.done = false; return; }
    const next = !opened;
    setOpened(next);
    if (next) {
      showSwipeHint();
      book.current.style.transform = "rotateY(-10deg) rotateX(1deg) translateY(-4px)";
      disc.current.style.transform = "translateX(-10px) translateZ(-80px) scaleX(1.12) scaleY(.88)";
    } else {
      book.current.style.transform = "rotateY(-8deg) rotateX(2deg) translateY(0) translateZ(0)";
      disc.current.style.transform = "";
    }
  };

  return (
    <motion.div
      ref={zone}
      className={`book-zone reveal show${opened ? " book-open" : ""}`}
      id="bookZone"
      data-testid="book-zone"
      initial={{ opacity: 0, y: 60, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 1.1, delay: 0.55, ease: [0.2, 0.7, 0.2, 1] }}
      onPointerMove={(e) => tilt(e.clientX, e.clientY)}
      onPointerLeave={reset}
      onTouchMove={(e) => { const t = e.touches?.[0]; if (t) tilt(t.clientX, t.clientY); }}
      onTouchEnd={reset}
    >
      <div className="book-orbit" />
      <div className="book-shadow-disc" ref={disc} />
      <div aria-label="eBook highlights" className="book-callout-rail">
        <span className="book-callout">
          <span className="book-callout-icon">52</span>
          <span><strong>Bite-sized lessons</strong><small>Practical, easy to revisit</small></span>
        </span>
        <span className="book-callout">
          <span className="book-callout-icon">5P</span>
          <span><strong>Purpose → People → Program → Place → Profit</strong><small>One connected framework</small></span>
        </span>
      </div>
      <div className="book-float">
      <div className={`book3d${opened ? " open" : ""}`} id="book3d" ref={book} onClick={toggle} onTouchStart={onSwipeStart} onTouchEnd={onSwipeEnd} data-testid="book-3d" role="button" tabIndex={0}>
        <div className="book-back" />
        <div className="book-pages" />
        <div className="book-inside">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div key={page} className="book-peek-page" initial={{ opacity: 0, x: 18 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -18 }} transition={{ duration: 0.28 }} data-testid="book-peek-page">
              <small>FREE PEEK INSIDE • {peek[0].toUpperCase()}</small>
              <img className="book-peek-img" src={peek[2]} alt={peek[1]} draggable={false} data-testid="book-peek-img" />
              <span className="book-peek-cap" data-testid="book-peek-title">{peek[1]}</span>
            </motion.div>
          </AnimatePresence>
          {swipeHint && (
            <div className="book-swipe-hint" data-testid="book-swipe-hint" aria-hidden="true">
              <span className="book-swipe-hand" />
              <span>Swipe to flip</span>
            </div>
          )}
          <div className="book-page-nav" onClick={(e) => e.stopPropagation()}>
            <span>Page {page + 1} of {PEEKS.length}</span>
            <div className="book-page-btns">
              <button type="button" className="book-page-btn" onClick={() => flip(-1)} aria-label="Previous page" data-testid="book-prev-page">←</button>
              <button type="button" className="book-page-btn" onClick={() => flip(1)} aria-label="Next page" data-testid="book-next-page">→</button>
            </div>
          </div>
        </div>
        <div className="book-front">
          <img alt="From Campus to Corporate eBook" src={images.cover} />
          <div className="book-cover-shine" ref={shine} />
          <div className="book-cover-sweep" />
          <div className="book-cover-edge" />
        </div>
        <div className="book-spine" />
      </div>
      </div>
      {opened ? (
        <div className="book-hint book-hint-controls" data-testid="book-controls">
          <span>Swipe or use the arrows to flip</span>
          <button type="button" className="book-close-btn" onClick={toggle} data-testid="book-close-btn">Close book</button>
        </div>
      ) : (
        <div className="book-hint">Move to tilt • Click to open</div>
      )}
    </motion.div>
  );
};
